import parsePhoneNumberFromString from 'libphonenumber-js/max';
import type { IntlShape } from 'react-intl/src/types';
import type { InferType } from 'yup';
import { array, bool, object, string } from 'yup';

import type { Country } from '@/types/regionTypes';

import type { UserFragment } from '@/graphql/saleor/api';
import { EMAIL_REGX, MAX_CHAR_PHONE } from '@/lib/forms/const';
import { getNameSchema, getTitleSchema } from '@/lib/forms/schemas';
import { ACCOUNT_METADATA } from '@/lib/metadata/const';
import { getMetaValue } from '@/lib/metadata/helpers';
import { getPhonePrefixSelectOptByCode } from '@/lib/phonePrefixes/helpers';
import { formMessages } from '@/messages/forms';

import {
  getUserPhonePrefix,
  getUserPhonePrefixLabel,
} from '../AppointmentView/helpers';
import type { PhonePrefix } from '../DeliveryView/helpers';

export type ArchivesSchema = InferType<ReturnType<typeof archivesSchema>> & {
  profileImgFile: FileList;
  reCaptchaResponse: string;
};

const MAX_FILES = 2;

export const archivesSchema = (
  formatMessage: IntlShape['formatMessage'],
  country: Country,
  user: Partial<UserFragment> | null,
) => {
  const requiredMsg = formatMessage(formMessages.requiredField);
  const invalidPhoneMsg = formatMessage(formMessages.invalidPhone);
  const invalidUploadMs = formatMessage(formMessages.archivesInvalidUpload);
  const uploadedFilesLimitMsg = formatMessage(
    formMessages.archivesInvalidUploadLimit,
  );
  let _title,
    _phone = '',
    _phonePrefix = {
      label: '',
      value: '',
    };
  if (user?.metadata) {
    _title = getMetaValue(user.metadata, ACCOUNT_METADATA.personalTitle);
    _phone = getMetaValue(user.metadata, ACCOUNT_METADATA.phone) ?? '';
    const userPhonePrefixValue = getUserPhonePrefix(user, country.code);
    const userPhonePrefixLabel = getUserPhonePrefixLabel(
      userPhonePrefixValue,
      _phone,
      country.code,
    );

    _phonePrefix = {
      label: userPhonePrefixLabel,
      value: userPhonePrefixValue,
    };
  }
  const MODEL_MIN_CHARS = 6;
  return object({
    modelReference: string()
      .required(requiredMsg)
      .default('')
      .min(
        MODEL_MIN_CHARS,
        formatMessage(formMessages.minChars, { min: MODEL_MIN_CHARS }),
      ),
    serialNumber: string().required(requiredMsg).default(''),
    title: getTitleSchema(formatMessage, country.code, _title),
    firstName: getNameSchema(formatMessage, 'first').default(
      user?.firstName ?? '',
    ),
    lastName: getNameSchema(formatMessage, 'last').default(
      user?.lastName ?? '',
    ),
    email: string()
      .required(requiredMsg)
      .matches(EMAIL_REGX, formatMessage(formMessages.invalidEmailAddress))
      .default(user?.email ?? ''),
    phone: string()
      .required(requiredMsg)
      .max(
        MAX_CHAR_PHONE,
        formatMessage(formMessages.maxChars, {
          max: MAX_CHAR_PHONE,
        }),
      )
      .when('phonePrefix', {
        is: (phonePrefix: PhonePrefix) => !!phonePrefix,
        then: (schema) =>
          schema.test('is-valid-phone', invalidPhoneMsg, function (value) {
            const { phonePrefix } = this.parent;
            const fullPhoneNumber = `${phonePrefix.value}${value}`;
            const phoneNumber = parsePhoneNumberFromString(fullPhoneNumber);
            return phoneNumber ? phoneNumber.isValid() : false;
          }),
        otherwise: () => string(),
      })
      .default(_phone),
    phonePrefix: object({
      value: string().required(requiredMsg),
      label: string().required(requiredMsg),
    })
      .required()
      .default(
        _phonePrefix.value
          ? _phonePrefix
          : getPhonePrefixSelectOptByCode(country.code),
      ),
    comment: string().default(''),
    attachments: array()
      .of(
        object().shape({
          base64: string(),
          file_name: string(),
          file_type: string(),
        }),
      )
      .min(MAX_FILES, uploadedFilesLimitMsg)
      .max(MAX_FILES, uploadedFilesLimitMsg)
      .required()
      .test('attachments', invalidUploadMs, function (value) {
        return value && value.length > 0;
      })
      .default([]),
    archivesConsent: bool().default(false).oneOf([true], requiredMsg),
    captcha: bool().default(false).oneOf([true], requiredMsg),
  });
};
